/**
 * tokenStore — the single accessor for browser-held auth values.
 *
 * useAuth and RpcClient store tokens in sessionStorage deliberately (narrower
 * XSS exposure window than localStorage, session clears on tab close). Every
 * reader must therefore look in sessionStorage FIRST; the localStorage
 * fallback only picks up a legacy token written by an older session and can be
 * dropped once none remain. Before issue #559 the two axios REST clients read
 * localStorage exclusively — which nothing writes — so every admin REST call
 * threw and every plain REST call went out unauthenticated.
 *
 * This module exists so the eventual removal of browser-held tokens entirely
 * (SSO, issue #590) edits ONE place. As of #601 S1-3 the writers
 * (src/hooks/useAuth.ts, src/services/rpc/RpcClient.ts) go through
 * setAuthValue/clearAuthValue too — no production code outside this file may
 * touch the auth keys in either storage directly.
 */

/**
 * Lane B session token. It was minted by `/api/v1/auth/login` (#707); that
 * endpoint is retired (#938, 410 Gone) and nothing mints a new one until NU
 * Core SSO (#590). Server-side, tokens without `exp` are refused.
 */
export const AUTH_TOKEN_KEY = "auth_token";
export const AUTH_USER_KEY = "auth_user";

/**
 * Lane A (`/rpc`) token — a SEPARATE slot, deliberately.
 *
 * The Lane B session JWT must never be sent to Lane A's host: they are
 * different origins with different trust, and since nudata-rpc-api v3.0.0
 * (D-4) Lane A accepts exactly one audience (`aud=nudata-rpc-api`), so a
 * Lane-B token is useless there anyway — sending it only exposes the
 * credential across a boundary. See #714.
 *
 * Nothing populates this yet: browser flows need an RFC 8693 gateway-exchanged
 * token, which is blocked on NUGUI-ORG/nugui-core-api#417. Until then Lane A
 * calls go unauthenticated and Lane A answers 401, which the Lane-A-owned
 * screens already render as their honest "unavailable" state.
 */
export const RPC_TOKEN_KEY = "rpc_token";

export function getAuthValue(key: string): string | null {
  if (typeof window === "undefined") return null;
  return window.sessionStorage.getItem(key) ?? window.localStorage.getItem(key);
}

/**
 * Write an auth value. sessionStorage ONLY — the deliberate #559 posture
 * (nothing writes localStorage anymore; the localStorage read above is a
 * legacy fallback, not a write target).
 */
export function setAuthValue(key: string, value: string): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(key, value);
}

export function clearAuthValue(key: string): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(key);
  window.localStorage.removeItem(key);
  notifyAuthCleared(key);
}

// ---------------------------------------------------------------------------
// Auth-cleared subscription seam (#601 S3 — POPI auto-lock)
// ---------------------------------------------------------------------------

// The base no-unused-vars rule false-flags parameter names inside TypeScript
// function-type annotations (see the NuDataTable.tsx note).
// eslint-disable-next-line no-unused-vars
export type AuthClearedListener = (key: string) => void;

const authClearedListeners = new Set<AuthClearedListener>();

/**
 * Subscribe to auth-value clears. Every logout / auth-expiry path in the app
 * funnels through clearAuthValue (useAuth, RpcClient, apiClient 401 handler),
 * so this is THE seam for "something must happen whenever auth goes away" —
 * the POPI layer uses it to re-lock consumer-data masking on session end.
 * Listener errors are swallowed so one bad subscriber can never break logout.
 * Returns an unsubscribe function.
 */
export function subscribeAuthCleared(
  listener: AuthClearedListener,
): () => void {
  authClearedListeners.add(listener);
  return () => {
    authClearedListeners.delete(listener);
  };
}

function notifyAuthCleared(key: string): void {
  for (const listener of authClearedListeners) {
    try {
      listener(key);
    } catch {
      // Never let a subscriber failure break the logout/expiry path.
    }
  }
}

/**
 * The #598 function seam for API clients: `tokenStore: getAuthToken`.
 * Sessions-first read of the bearer token, never a storage key string.
 */
export function getAuthToken(): string | null {
  return getAuthValue(AUTH_TOKEN_KEY);
}
